import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { languages, type LanguageCode } from '../i18n';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook for managing language preferences.
 * Synchronizes language between frontend (i18n) and backend (user preferences).
 */
/**
 * Manage language preferences and persist changes when authenticated.
 */
export function useLanguage() {
  const { i18n } = useTranslation();
  const { user, isAuthenticated, updateUser } = useAuth();

  // Sync language from user data on login
  useEffect(() => {
    if (isAuthenticated && user?.language) {
      // User has a language preference saved in the database
      if (i18n.language !== user.language) {
        i18n.changeLanguage(user.language);
        localStorage.setItem('language', user.language);
      }
    }
  }, [isAuthenticated, user?.language, i18n]);

  // Keep document language in sync to reduce browser auto-translate prompts
  useEffect(() => {
    if (i18n.language) {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  /**
   * Change language and save to backend
   */
  const setLanguage = useCallback(async (lang: LanguageCode) => {
    // Update frontend immediately
    await i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);

    // Save to backend if authenticated
    if (isAuthenticated) {
      try {
        const response = await api.patch('/api/account/preferences/', { language: lang });
        const savedLanguage = response.data?.language ?? lang;
        updateUser({ language: savedLanguage });
      } catch (error) {
        console.error('Failed to save language preference:', error);
        // Language is already changed locally, so user can continue
      }
    }
  }, [i18n, isAuthenticated, updateUser]);

  /**
   * Get language name in native language
   */
  const getLanguageName = useCallback((code: LanguageCode): string => {
    const lang = languages.find(l => l.code === code);
    return lang?.nativeName || code;
  }, []);

  return {
    /** Current language code */
    language: i18n.language as LanguageCode,
    /** Change language and save to backend */
    setLanguage,
    /** List of available languages */
    availableLanguages: languages,
    /** Get language name in native language */
    getLanguageName,
    /** Check if translations are ready */
    isReady: i18n.isInitialized,
  };
}

export default useLanguage;
